
import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import ArticleTags from '@root/modules/article/components/ArticleTags.vue';
import ArticleSource from '@root/modules/article/components/ArticleSource.vue';
import ArticleTeliaProjectScroller from '@root/modules/article/components/projects/ArticleTeliaProjectScroller.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';
import getArticleSources from '@root/modules/article/utils/getArticleSources';
import { Source } from '@root/common/types/fragment';
import { faChevronDown, faChevronUp } from '@delfi.lt/fa-kit/icons/modules/classic/regular';
import { faScaleUnbalancedFlip } from '@delfi.lt/fa-kit/icons/modules/classic/solid';

interface Props {
  article: ArticleData;
}

interface Data {
  isClient: boolean;
  showComments: boolean;
  sourcesActive: boolean;
  tagsActive: boolean;
}

interface Computed {
  articleUrl: string;
  articleCommentsPath: string;
  disableAds: boolean;
  disableComments: boolean;
  isCrArticle: boolean;
  showBottomText: boolean;
  showOpinionDisclaimer: boolean;
  sources: Source[];
}

interface Methods {
  getKey: (params: Record<string, any>) => string;
  toggleCollapse: (section: string) => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleTags,
    ArticleSource,
    AdFormBanner,
    ArticleTeliaProjectScroller,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isClient: process.client,
      showComments: false,
      sourcesActive: true,
      tagsActive: false,
      faChevronDown,
      faChevronUp,
      faScaleUnbalancedFlip,
    };
  },
  computed: {
    articleUrl() {
      const { domain, locale } = this.$channelConfig('settings');
      return this.$options.filters!.articleUrl({ article: this.article, locale, domain, fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED });
    },
    articleCommentsPath() {
      const { domain, locale } = this.$channelConfig('settings');
      return this.$options.filters!.articleCommentsUrl({ article: this.article, locale, domain, fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED });
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    disableComments() {
      return !this.article.comments.disableComments;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    showBottomText() {
      const { showBottomText } = this.$channelConfig('page').article.component;

      return showBottomText || false;
    },
    showOpinionDisclaimer() {
      const opinionCategories: number[] = [10459511];
      if (this.article.content.paywall.enabled) {
        return (
          (opinionCategories.includes(this.article.primaryCategory.id) || opinionCategories.includes(this.article.primaryCategory.parentCategory.id)) &&
          this.article.content.paywall.access
        );
      }

      return opinionCategories.includes(this.article.primaryCategory.id) || opinionCategories.includes(this.article.primaryCategory.parentCategory.id);
    },
    sources() {
      return getArticleSources(this.article);
    },
  },
  methods: {
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
    toggleCollapse(section) {
      // Switch active state
      if (section === 'source') {
        this.sourcesActive = !this.sourcesActive;

        if (this.sourcesActive) {
          this.tagsActive = false;
        }
      } else {
        this.tagsActive = !this.tagsActive;

        if (this.tagsActive) {
          this.sourcesActive = false;
        }
      }

      const id = this.article.id;
      const targetNavigationItem = window.document.querySelector(`.article-body-bottom__collapse-navigation--item-${section}-${id}`) as HTMLElement;
      const targetContainer = window.document.querySelector(`.article-body-bottom__collapse-container--${section}-${id}`) as HTMLElement;
      const targetContainerIsActive = targetContainer.classList.contains('article-body-bottom__collapse-container--active');

      const navigationItems = window.document.querySelectorAll('.article-body-bottom__collapse-navigation--item');
      navigationItems.forEach((element) => {
        const navigationItem = element as HTMLElement;
        navigationItem.classList.remove('article-body-bottom__collapse-navigation--item--active');
      });

      const containers = window.document.querySelectorAll('.article-body-bottom__collapse-container');

      containers.forEach((element) => {
        const container = element as HTMLElement;
        container.classList.remove('article-body-bottom__collapse-container--active');
      });

      if (!targetContainerIsActive) {
        targetNavigationItem.classList.toggle('article-body-bottom__collapse-navigation--item--active');
        targetContainer.classList.toggle('article-body-bottom__collapse-container--active');
      }
    },
  },
});
